import {
  Component,
  effect,
  inject,
  input,
  output,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';
import { MarketingPricingService, NeighborhoodComparison } from 'src/shared/services/marketing-pricing.service';

@Component({
  selector: 'pricing-neighborhood',
  standalone: true,
  imports: [CommonModule, TidyUiComponentsModule, SharedModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  template: `
    @if (comparison) {
      <!-- Bedrooms belongs to the listing: the market averages come from the
           pricing provider's own pick of nearby listings, of any bedroom count. -->
      <tidy-card>
        <tidy-row class="extra-bottom-padding">
          <tidy-text [header]="'Your Listing'" />
        </tidy-row>
        <tidy-row [alignLastItemRight]="true">
          <tidy-text [body]="'Bedrooms'" />
          <tidy-text [body]="(comparison.bedrooms ?? '-') + ''" />
        </tidy-row>
        <tidy-row [alignLastItemRight]="true">
          <tidy-text [body]="'Nightly Rate'" />
          <tidy-text [body]="formatCents(comparison.avgListingRate)" />
        </tidy-row>
        <tidy-row [alignLastItemRight]="true">
          <tidy-text [body]="'Occupancy'" />
          <tidy-text [body]="comparison.occupancyRate + '%'" />
        </tidy-row>
        <tidy-row [alignLastItemRight]="true">
          <tidy-text [body]="'Est. Monthly Revenue'" />
          <tidy-text [body]="formatCents(comparison.monthlyRevenue)" />
        </tidy-row>
      </tidy-card>

      <tidy-card>
        <tidy-row class="extra-bottom-padding">
          <tidy-text [header]="'Market Average'" />
        </tidy-row>
        <tidy-row [alignLastItemRight]="true">
          <tidy-text [body]="'Nightly Rate'" />
          <tidy-text [body]="formatCents(comparison.avgAreaRate)" />
        </tidy-row>
        <tidy-row [alignLastItemRight]="true">
          <tidy-text [body]="'Occupancy'" />
          <tidy-text [body]="comparison.avgAreaOccupancy + '%'" />
        </tidy-row>
        <tidy-row [alignLastItemRight]="true">
          <tidy-text [body]="'Avg. Monthly Revenue'" />
          <tidy-text [body]="formatCents(comparison.avgAreaRevenue)" />
        </tidy-row>
      </tidy-card>
    }
  `
})
export class PricingNeighborhoodComponent {
  address = input.required<any>();
  mock = input<boolean>(false);
  loaded = output<void>();

  private readonly pricingService = inject(MarketingPricingService);
  private readonly cdr = inject(ChangeDetectorRef);

  comparison: NeighborhoodComparison;

  constructor() {
    effect(() => {
      this.address();
      this.mock();
      this.comparison = null;
      this.cdr.markForCheck();
      this.loadData();
    });
  }

  async loadData() {
    try {
      this.comparison = this.mock()
        ? this.buildMockComparison()
        : await this.pricingService.getNeighborhoodData(this.address().id);
      this.cdr.markForCheck();
    } catch (err) {
      console.error('Failed to load neighborhood data', err);
    } finally {
      this.loaded.emit();
    }
  }

  private buildMockComparison(): NeighborhoodComparison {
    return {
      bedrooms: 2,
      avgListingRate: 26500,
      occupancyRate: 71,
      monthlyRevenue: 565000,
      avgAreaRate: 24900,
      avgAreaOccupancy: 64,
      avgAreaRevenue: 478000,
    } as NeighborhoodComparison;
  }

  formatCents(cents: number): string {
    if (cents === null || cents === undefined) {return '-';}
    return `$${  (cents / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }
}
